import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageContent, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, RefreshCw, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";

const blankLive = {
  name: "",
  stream_url: "",
  telegram_stream_key: "",
  auto_restart: true,
  active: true,
};

function RuntimeBadge({ live }) {
  const running = live?.runtime?.running;
  return (
    <span className={running ? "text-emerald-300" : "text-slate-400"}>
      {running ? "running" : live?.status || "stopped"}
    </span>
  );
}

export default function TelegramLive() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blankLive);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/telegram-live");
    setItems(data || []);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore caricamento Telegram Live"));
  }, []);

  const save = async () => {
    if (!form.name || !form.stream_url || !form.telegram_stream_key) {
      toast.error("Nome, stream URL e stream key sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      await api.post("/telegram-live", form);
      setForm(blankLive);
      await load();
      toast.success("Live creata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Creazione live non riuscita");
    } finally {
      setLoading(false);
    }
  };

  const action = async (id, name) => {
    setLoading(true);
    try {
      await api.post(`/telegram-live/${id}/${name}`);
      await load();
      toast.success(`Comando ${name} inviato`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Comando non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/telegram-live/${id}`);
      await load();
      toast.success("Live eliminata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Eliminazione live non riuscita");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="telegram-live-page">
      <PageHeader
        title="Telegram Live"
        subtitle="Controllo professionale dei processi FFmpeg indipendenti per live Telegram."
      />
      <PageContent>
        <div className="bg-slate-900 border border-slate-800 rounded-md p-4 grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Stream URL</Label>
            <Input value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} />
          </div>
          <div>
            <Label>Telegram Stream Key</Label>
            <Input
              type="password"
              value={form.telegram_stream_key}
              onChange={(e) => setForm({ ...form, telegram_stream_key: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <Button className="w-full" onClick={save} disabled={loading}>Crea Live</Button>
          </div>
        </div>

        <div className="space-y-3">
          {items.map((live) => (
            <div key={live.id} className="bg-slate-900 border border-slate-800 rounded-md p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="font-bold text-white">{live.name}</div>
                  <div className="text-xs text-slate-500 truncate">{live.stream_url}</div>
                  <div className="mt-1 text-xs font-mono">
                    Stato: <RuntimeBadge live={live} /> · PID: {live.runtime?.pid || "-"} · Uptime:{" "}
                    {live.runtime?.uptime_seconds || 0}s
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => action(live.id, "start")} disabled={loading}>
                    <Play size={14} /> Avvia
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => action(live.id, "restart")} disabled={loading}>
                    <RefreshCw size={14} /> Restart
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => action(live.id, "stop")} disabled={loading}>
                    <Square size={14} /> Stop
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={() => remove(live.id)} disabled={loading}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <pre className="mt-3 max-h-44 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-400">
                {(live.runtime?.logs || []).join("\n") || "Nessun log FFmpeg disponibile."}
              </pre>
            </div>
          ))}
          {items.length === 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-md p-8 text-center text-slate-500">
              Nessuna live configurata.
            </div>
          )}
        </div>
      </PageContent>
    </div>
  );
}
